import { FontWeight, Heading } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { DirectValuesProposal } from '@Pages/passwordless/welcome/components/valueProposal/DirectValuesProposal.tsx';
import { ValueProposalWyoming } from '@Pages/passwordless/welcome/components/valueProposal/ValueProposalWyoming.tsx';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { DirectWelcomeHeader } from '@Pages/passwordless/welcome/components/DirectWelcomeHeader.tsx';
import { PASSWORD_LESS_PARTNER_IDS_MAP, SAGE_PARTNER_IDS } from '@Types/consts';
import { WelcomeForm } from '@Pages/passwordless/welcome/welcomeForm/WelcomeForm.tsx';
import { ValuesProposalPasswordless } from './components/valueProposal/ValuesProposalPasswordless.tsx';
import { WelcomeBonusChip } from './components/WelcomeBonusChip.tsx';
import { useIsDnbEmbedded } from './hooks/useIsDnbEmbedded';
import type { SagePartnerIds } from '@Types/consts';

const DNB_EMBEDDED_WELCOME_BACKGROUND =
  'linear-gradient(0deg, var(--Blue-200, #CFDFFF) 0%, var(--Grey-000, #FFF) 77.38%)';

export const WelcomePage = () => {
  const { t } = useTranslation();
  const isEmbedded = useBoundStore((state) => state.onboardingSlice.getIsEmbedded());
  const partnerId = useBoundStore((state) => state.customer.partnerId);
  const setCustomerAccessToken = useBoundStore((state) => state.setCustomerAccessToken);
  const setAuthUserAccessToken = useBoundStore((state) => state.setAuthUserAccessToken);
  const isPartnerFlow = useBoundStore((state) => state.onboardingSlice.isPartnerFlow);
  const setIsAppSubmitted = useBoundStore((state) => state.onboardingSlice.setIsAppSubmitted);

  // D&B embedded sign-up shows the form on its own, centered, with no right side panel.
  const isDnbEmbedded = useIsDnbEmbedded();

  const getValueProposalComponent = () => {
    if (!isEmbedded) {
      return <DirectValuesProposal />;
    }

    switch (partnerId) {
      case PASSWORD_LESS_PARTNER_IDS_MAP.WYOMING_INTEG:
      case PASSWORD_LESS_PARTNER_IDS_MAP.WYOMING_SAGE:
        return <ValueProposalWyoming />;
      case PASSWORD_LESS_PARTNER_IDS_MAP.LILI_PARTNER:
      default:
        return <ValuesProposalPasswordless />;
    }
  };

  // Clear access tokens when Welcome Page mounts, but NOT in partner flow
  useEffect(() => {
    // First check partnerId, then fallback to isPartnerFlow
    const shouldPreserveTokens = partnerId || isPartnerFlow;

    if (!shouldPreserveTokens) {
      setCustomerAccessToken('');
      setAuthUserAccessToken('');
      useBoundStore.getState().setOnboardingAuthenticated(false);
    }
  }, [setCustomerAccessToken, setAuthUserAccessToken, partnerId, isPartnerFlow]);

  useEffect(() => {
    setIsAppSubmitted(false);
  }, []);

  return (
    <>
      {isDnbEmbedded && (
        <div
          aria-hidden='true'
          data-testid='welcome-dnb-background'
          className='fixed inset-0 -z-10 pointer-events-none'
          style={{ background: DNB_EMBEDDED_WELCOME_BACKGROUND }}
        />
      )}
      <div
        id='welcome-page-container'
        className={`flex flex-col tab-landscape:flex-row items-center ${isDnbEmbedded ? 'tab-landscape:items-start' : 'tab-landscape:items-stretch'} w-full h-dvh overflow-y-scroll overflow-x-hidden fade-in`}>
        <div
          id='welcome-form-section'
          data-testid='welcome-form-section'
          className={`flex justify-center w-full shrink-0 h-fit ${isDnbEmbedded ? 'items-center tab-landscape:min-h-full' : 'tab-landscape:h-full'} ${isDnbEmbedded ? 'bg-transparent' : 'bg-surface-primary-bw-00'} md:px-[24px] px-[16px] md:pt-[100px] pt-[16px] pb-[32px] ${isDnbEmbedded ? 'tab-landscape:basis-full' : 'tab-landscape:basis-1/2'}`}>
          <div
            className={
              isDnbEmbedded
                ? 'flex flex-col max-w-[420px] w-full'
                : 'flex flex-col h-full max-w-[420px] w-full justify-center'
            }>
            {isEmbedded && SAGE_PARTNER_IDS.includes(partnerId as SagePartnerIds) && (
              <div className='sm:hidden self-center mb-[12px]'>
                <WelcomeBonusChip />
              </div>
            )}
            <div className='mb-[28px]'>
              {isEmbedded ? (
                <>
                  <div className='hidden sm:block text-center'>
                    <Heading.H5 fontWeight={FontWeight.SEMIBOLD}>
                      {t('pages.homePasswordless.title')}
                    </Heading.H5>
                  </div>
                  <div className='sm:hidden text-center'>
                    <Heading.H7 fontWeight={FontWeight.SEMIBOLD}>
                      {t('pages.homePasswordless.title')}
                    </Heading.H7>
                  </div>
                </>
              ) : (
                <DirectWelcomeHeader />
              )}
            </div>
            <WelcomeForm />
          </div>
        </div>
        {!isDnbEmbedded && (
          <div
            id='welcome-value-proposal-section'
            className='flex justify-center w-full tab-landscape:max-h-[100dvh] tab-landscape:overflow-auto overflow-x-hidden tab-landscape:basis-1/2 shrink-0 tab-landscape:min-h-full grow bg-surface-brand-static-lili'>
            {getValueProposalComponent()}
          </div>
        )}
      </div>
    </>
  );
};
